import React, { useRef, useEffect, useMemo } from 'react'
import { useGLTF, useAnimations } from '@react-three/drei'
import * as THREE from 'three'

// Final qiymatlar
const HUE = 1.80
const SATURATION = 0
const BRIGHTNESS = 0

// Hue shift shader - faqat Model1 uchun
const createHueShiftMaterial = (originalMaterial) => {
  const material = new THREE.MeshStandardMaterial()

  // Original material xususiyatlarini copy qilish
  if (originalMaterial.map) material.map = originalMaterial.map
  if (originalMaterial.normalMap) material.normalMap = originalMaterial.normalMap
  if (originalMaterial.roughnessMap) material.roughnessMap = originalMaterial.roughnessMap
  if (originalMaterial.metalnessMap) material.metalnessMap = originalMaterial.metalnessMap
  if (originalMaterial.emissiveMap) material.emissiveMap = originalMaterial.emissiveMap

  material.metalness = originalMaterial.metalness || 0
  material.roughness = originalMaterial.roughness || 0.5
  material.emissive = originalMaterial.emissive || new THREE.Color(0x000000)
  material.emissiveIntensity = originalMaterial.emissiveIntensity || 1

  // Custom shader qo'shish
  material.onBeforeCompile = (shader) => {
    shader.uniforms.uHueShift = { value: HUE }
    shader.uniforms.uSaturation = { value: SATURATION }
    shader.uniforms.uBrightness = { value: BRIGHTNESS }

    // Fragment shader ga hue shift function qo'shish
    shader.fragmentShader = shader.fragmentShader.replace(
      '#include <common>',
      `#include <common>
      uniform float uHueShift;
      uniform float uSaturation;
      uniform float uBrightness;

      vec3 rgb2hsv(vec3 c) {
        vec4 K = vec4(0.0, -1.0 / 3.0, 2.0 / 3.0, -1.0);
        vec4 p = mix(vec4(c.bg, K.wz), vec4(c.gb, K.xy), step(c.b, c.g));
        vec4 q = mix(vec4(p.xyw, c.r), vec4(c.r, p.yzx), step(p.x, c.r));
        float d = q.x - min(q.w, q.y);
        float e = 1.0e-10;
        return vec3(abs(q.z + (q.w - q.y) / (6.0 * d + e)), d / (q.x + e), q.x);
      }

      vec3 hsv2rgb(vec3 c) {
        vec4 K = vec4(1.0, 2.0 / 3.0, 1.0 / 3.0, 3.0);
        vec3 p = abs(fract(c.xxx + K.xyz) * 6.0 - K.www);
        return c.z * mix(K.xxx, clamp(p - K.xxx, 0.0, 1.0), c.y);
      }

      vec3 applyColorGrading(vec3 color, float hueShift, float saturation, float brightness) {
        vec3 hsv = rgb2hsv(color);
        hsv.x = fract(hsv.x + hueShift / (2.0 * 3.14159265));
        hsv.y = clamp(hsv.y + saturation, 0.0, 1.0);
        hsv.z = clamp(hsv.z + brightness, 0.0, 1.0);

        // Yashil rangni blue ga almashtirish
        float greenHue = hsv.x;
        if (greenHue > 0.2 && greenHue < 0.45) {
          hsv.x = 0.58;  // Blue hue (cyan-blue)
        }

        return hsv2rgb(hsv);
      }
      `
    )

    // Output color ga color grading qo'llash
    shader.fragmentShader = shader.fragmentShader.replace(
      '#include <dithering_fragment>',
      `#include <dithering_fragment>
      gl_FragColor.rgb = applyColorGrading(gl_FragColor.rgb, uHueShift, uSaturation, uBrightness);
      `
    )
  }

  return material
}

export default function Model1(props) {
  const group = useRef()
  const { nodes, materials, animations } = useGLTF('/model1.glb')
  const { actions } = useAnimations(animations, group)

  useEffect(() => {
    const action = actions[Object.keys(actions)[0]]
    if (action) {
      action.play()
      action.timeScale = 0.5
    }
  }, [actions])

  // Hue shifted materiallar yaratish
  const hueShiftedMaterials = useMemo(() => ({
    Ch44_Body: createHueShiftMaterial(materials.Ch44_Body),
    Ch44_body1: createHueShiftMaterial(materials.Ch44_body1),
  }), [materials])

  return (
    <group ref={group} {...props} dispose={null}>
      <group name="Sketchfab_Scene" >
        <group name="Sketchfab_model" rotation={[-Math.PI / 2, 0, 0]} scale={0.017}>
          <group name="e779cecba394439eb4f3b40699d8d016fbx" rotation={[Math.PI / 2, 0, 0]}>
            <group name="Object_2">
              <group name="RootNode">
                <group name="Object_4">
                  <primitive object={nodes._rootJoint} />
                  <skinnedMesh
                    name="Object_6"
                    geometry={nodes.Object_6.geometry}
                    material={hueShiftedMaterials.Ch44_Body}
                    skeleton={nodes.Object_6.skeleton}
                  />
                  <skinnedMesh
                    name="Object_7"
                    geometry={nodes.Object_7.geometry}
                    material={hueShiftedMaterials.Ch44_body1}
                    skeleton={nodes.Object_7.skeleton}
                  />
                  <group name="Ch44" />
                </group>
              </group>
            </group>
          </group>
        </group>
      </group>
    </group>
  )
}

useGLTF.preload('/model1.glb')
